import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, ChevronDown, Shield, Building2, DollarSign,
  AlertTriangle, FileText, ClipboardList, MessageCircle,
  CheckCircle2, Users, TrendingUp, Calendar, Lock, Phone
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';

import { FAQSchema } from '../components/StructuredData';

// FAQ Accordion Item Component
const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <motion.div 
      className="border-b border-white/10 last:border-b-0"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <button
        onClick={onClick}
        className="w-full py-5 flex items-center justify-between gap-4 text-left group"
      >
        <span className="text-white font-medium group-hover:text-[#D4AF37] transition-colors">
          {question}
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex-shrink-0"
        >
          <ChevronDown className={`h-5 w-5 transition-colors ${isOpen ? 'text-[#D4AF37]' : 'text-white/40'}`} />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="pb-5 text-white/60 text-sm leading-relaxed">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// FAQ Section Component
const FAQSection = ({ icon: Icon, title, faqs, openIndex, setOpenIndex, sectionIndex }) => {
  const handleClick = (index) => {
    const globalIndex = `${sectionIndex}-${index}`;
    setOpenIndex(openIndex === globalIndex ? null : globalIndex);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="mb-12"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/20">
          <Icon className="h-5 w-5 text-[#D4AF37]" />
        </div>
        <h2 className="text-xl font-bold text-white">{title}</h2>
      </div>
      <div className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl border border-white/10 px-6">
        {faqs.map((faq, index) => (
          <FAQItem
            key={index}
            question={faq.question}
            answer={faq.answer}
            isOpen={openIndex === `${sectionIndex}-${index}`}
            onClick={() => handleClick(index)}
          />
        ))}
      </div>
    </motion.div>
  );
};

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  // FAQ Data organized by section
  const faqSections = [
    {
      icon: Users,
      title: 'Investor Eligibility',
      faqs: [
        {
          question: 'What is an investor?',
          answer: (
            <>
              An investor is defined by SEC regulations and generally includes individuals with:
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Annual income exceeding $200,000 (or $300,000 with spouse) for the past two years with expectation of the same</li>
                <li>Net worth exceeding $1 million, excluding primary residence</li>
                <li>Certain professional certifications, designations, or credentials</li>
              </ul>
              <p className="mt-2">Entities may also qualify based on assets or ownership structure.</p>
            </>
          )
        },
        {
          question: 'How is accreditation verified under Regulation D 506(c)?',
          answer: 'Under SEC Rule 506(c), we are required to take reasonable steps to verify accredited investor status. This typically involves reviewing tax returns, W-2s, bank statements, or obtaining written confirmation from a licensed attorney, CPA, or registered investment advisor.'
        },
        {
          question: 'Can I invest through an IRA, LLC, or trust?',
          answer: 'Yes, investments can be made through self-directed IRAs, LLCs, trusts, and other qualified entities. The entity must meet investor requirements. We recommend consulting with your tax advisor regarding the optimal structure for your situation.'
        }
      ]
    },
    {
      icon: Building2,
      title: 'Fund Structure',
      faqs: [
        {
          question: 'How is the fund structured?',
          answer: (
            <>
              ASAP Capital operates a Regulation D 506(c) private placement fund structured as a limited partnership. The General Partner (GP) manages investments while Limited Partners (LPs) provide capital and receive distributions. <Link to="/fund-terms" className="text-[#D4AF37] hover:underline">View complete fund terms →</Link>
            </>
          )
        },
        {
          question: 'What is the minimum investment?',
          answer: 'The minimum investment is $100,000 for individual investors. Higher minimums may apply for certain investment structures such as co-investments or separate accounts.'
        },
        {
          question: 'What is the expected investment period?',
          answer: 'Our fund targets a 5-7 year investment period, though actual hold times vary by property based on business plan execution and market conditions. Early exits are possible when optimal returns can be achieved sooner.'
        },
        {
          question: 'What are the fees?',
          answer: 'The fund charges a 1.5% annual management fee and 20% carried interest above an 8% preferred return. This alignment ensures we only earn incentive compensation after delivering returns to investors. Full fee details are in the PPM.'
        }
      ]
    },
    {
      icon: TrendingUp,
      title: 'Returns & Distributions',
      faqs: [
        {
          question: 'How are returns generated?',
          answer: (
            <>
              Returns come from two sources: (1) ongoing cash flow from property operations distributed quarterly, and (2) capital gains upon property sale. Our value-add strategy focuses on acquiring underperforming assets, implementing improvements, and capturing increased rents and property values. <Link to="/strategy" className="text-[#D4AF37] hover:underline">Learn about our strategy →</Link>
            </>
          )
        },
        {
          question: 'How often are distributions paid?',
          answer: 'We target quarterly distributions from property cash flow, subject to property performance and reserve requirements. Capital gains are distributed following property sales. Actual distributions depend on investment performance and fund needs.'
        },
        {
          question: 'What is the preferred return?',
          answer: 'Investors receive an 8% preferred return, meaning the first 8% of profits are distributed to LPs before the GP participates in carried interest. This structure aligns sponsor and investor interests.'
        },
        {
          question: 'What performance metrics are reported?',
          answer: (
            <>
              We report IRR (Internal Rate of Return), equity multiple, cash-on-cash yield, and property-level operating metrics including occupancy, NOI, and capital improvement progress. <Link to="/track-record" className="text-[#D4AF37] hover:underline">View our track record →</Link>
            </>
          )
        }
      ]
    },
    {
      icon: AlertTriangle,
      title: 'Risks & Capital',
      faqs: [
        {
          question: 'What are the risks of real estate investing?',
          answer: 'Real estate investments involve significant risks including: market value fluctuations, economic downturns, tenant defaults, interest rate changes, construction/renovation delays, regulatory changes, and illiquidity. Investors should be prepared for potential loss of some or all invested capital.'
        },
        {
          question: 'Can I lose money?',
          answer: 'Yes. All investments involve risk, including the potential loss of principal. While we employ conservative underwriting and risk management practices, there is no guarantee of returns or capital preservation. Past performance is not indicative of future results.'
        },
        {
          question: 'How does ASAP Capital manage risk?',
          answer: (
            <>
              We employ multiple risk mitigation strategies:
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Conservative leverage with fixed-rate financing</li>
                <li>Stress-tested underwriting with margin of safety</li>
                <li>Diversification across properties and submarkets</li>
                <li>In-house property management for operational control</li>
                <li>Adequate capital reserves for contingencies</li>
              </ul>
            </>
          )
        },
        {
          question: 'Are my investments liquid?',
          answer: 'No. Private real estate investments are illiquid. There is no secondary market for fund interests, and investors should expect capital to be committed for the full investment period (typically 5-7 years). These investments are suitable only for investors who do not need liquidity.'
        }
      ]
    },
    {
      icon: FileText,
      title: 'Tax Considerations',
      faqs: [
        {
          question: 'What tax documents will I receive?',
          answer: 'Investors receive an annual Schedule K-1 reporting their share of partnership income, losses, deductions, and credits. K-1s are typically issued by March 15th following the tax year. We recommend consulting with a qualified tax advisor.'
        },
        {
          question: 'Are there tax advantages to real estate investments?',
          answer: 'Real estate investments may offer tax benefits including depreciation deductions, 1031 exchange opportunities, and potential long-term capital gains treatment. Tax implications vary based on individual circumstances. This information is not tax advice—please consult a qualified tax professional.'
        },
        {
          question: 'How does depreciation work?',
          answer: 'Real estate depreciation allows investors to deduct a portion of property value annually, potentially reducing taxable income even when receiving cash distributions. This can result in tax-advantaged income, though depreciation is recaptured upon sale. Consult your tax advisor for personalized guidance.'
        }
      ]
    },
    {
      icon: ClipboardList,
      title: 'Investment Process',
      faqs: [
        {
          question: 'What are the steps to invest?',
          answer: (
            <>
              The investment process includes:
              <ol className="list-decimal pl-5 mt-2 space-y-1">
                <li>Initial consultation to discuss goals and fit</li>
                <li>Review of Private Placement Memorandum (PPM)</li>
                <li>Accreditation verification</li>
                <li>Completion of subscription documents</li>
                <li>Capital commitment and funding</li>
              </ol>
              <Link to="/investment-process" className="text-[#D4AF37] hover:underline block mt-2">View detailed process →</Link>
            </>
          )
        },
        {
          question: 'When is capital called?',
          answer: 'Capital is typically called at subscription closing. For larger commitments, we may utilize a capital call structure where funds are drawn as investment opportunities arise. Specific terms are outlined in the subscription agreement.'
        },
        {
          question: 'How do I access my investor portal?',
          answer: 'After closing, investors receive secure login credentials for our investor portal. The portal provides access to account statements, distribution notices, quarterly reports, K-1 documents, and property updates.'
        }
      ]
    },
    {
      icon: MessageCircle,
      title: 'Transparency & Updates',
      faqs: [
        {
          question: 'How often do I receive updates?',
          answer: 'Investors receive quarterly reports with detailed property-level performance, financial statements, and market commentary. We also provide timely updates on significant events, capital improvements, and portfolio milestones.'
        },
        {
          question: 'What information is shared with investors?',
          answer: 'Our reporting includes: property occupancy and rent trends, NOI performance, capital improvement progress, market conditions, distribution summaries, and portfolio valuation updates. All offering documents and historical reports are available through the investor portal.'
        },
        {
          question: 'How can I contact the team?',
          answer: (
            <>
              Investors have direct access to our team via phone, email, or scheduled meetings. We pride ourselves on accessibility and responsiveness. <Link to="/contact" className="text-[#D4AF37] hover:underline">Contact us →</Link>
            </>
          )
        }
      ]
    }
  ];

  // Flatten FAQs for schema
  const allFaqs = faqSections.flatMap(section => 
    section.faqs.map(faq => ({
      question: faq.question,
      // Convert React elements to plain text for schema
      answer: typeof faq.answer === 'string' ? faq.answer : 
        faq.answer?.props?.children ? 
          (Array.isArray(faq.answer.props.children) 
            ? faq.answer.props.children.filter(c => typeof c === 'string').join(' ')
            : String(faq.answer.props.children))
          : ''
    }))
  );

  return (
    <>
      <Helmet>
        <title>Real Estate Investment FAQ for investors | ASAP Capital</title>
        <meta 
          name="description" 
          content="Find answers to common questions about investing with ASAP Capital, including accreditation, risks, taxes, and distributions." 
        />
        <meta name="keywords" content="real estate investment FAQ, investor requirements, private equity real estate, investment risks, tax considerations, 506c fund" />
        <link rel="canonical" href="/faq" />
      </Helmet>
      
      {/* FAQ Structured Data for SEO */}
      <FAQSchema faqs={allFaqs} />

      <div className="min-h-screen bg-[#0A1628]">
        <Header />
        
        <main>
          {/* ==================== HERO SECTION ==================== */}
          <section className="relative pt-36 pb-16 lg:pt-44 lg:pb-24 overflow-hidden">
            {/* Background elements */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] via-[#0d1a2d] to-[#0A1628]" />
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#D4AF37]/5 rounded-full blur-[150px] pointer-events-none" />
            
            <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="text-center max-w-3xl mx-auto"
              >
                {/* Pill badge */}
                <motion.div 
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 bg-[#D4AF37]/10 border border-[#D4AF37]/20"
                  variants={itemVariants}
                >
                  <MessageCircle className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-xs font-semibold text-[#D4AF37] uppercase tracking-wider">Investor FAQ</span>
                </motion.div>

                {/* H1 */}
                <motion.h1 
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.2] mb-6"
                  variants={itemVariants}
                >
                  Frequently Asked{' '}
                  <span className="bg-gradient-to-r from-[#D4AF37] via-[#F5D76E] to-[#D4AF37] bg-clip-text text-transparent">
                    Questions
                  </span>
                </motion.h1>

                {/* Subtitle */}
                <motion.p 
                  className="text-base lg:text-lg text-white/60 leading-relaxed mb-8"
                  variants={itemVariants}
                >
                  Everything you need to know about investing with ASAP Capital—from eligibility 
                  and fund structure to returns, risks, and getting started.
                </motion.p>

                {/* Quick nav */}
                <motion.div 
                  className="flex flex-wrap justify-center gap-2"
                  variants={itemVariants}
                >
                  {faqSections.map((section, index) => (
                    <a
                      key={index}
                      href={`#section-${index}`}
                      className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/60 text-xs hover:bg-[#D4AF37]/10 hover:border-[#D4AF37]/30 hover:text-[#D4AF37] transition-all"
                    >
                      {section.title}
                    </a>
                  ))}
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== FAQ SECTIONS ==================== */}
          <section className="py-12 lg:py-20 relative">
            <div className="max-w-[900px] mx-auto px-6 lg:px-12">
              {faqSections.map((section, index) => (
                <div key={index} id={`section-${index}`} className="scroll-mt-32">
                  <FAQSection
                    icon={section.icon}
                    title={section.title}
                    faqs={section.faqs}
                    openIndex={openIndex}
                    setOpenIndex={setOpenIndex}
                    sectionIndex={index}
                  />
                </div>
              ))}
            </div>
          </section>

          {/* ==================== DISCLAIMER ==================== */}
          <section className="py-8 relative">
            <div className="max-w-[900px] mx-auto px-6 lg:px-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5"
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="text-white/50 text-xs leading-relaxed">
                    <strong className="text-amber-400">Disclaimer:</strong> The information on this page is for educational purposes only and does not constitute investment, tax, or legal advice. All investments involve risk, including the potential loss of principal. The Private Placement Memorandum (PPM) contains complete terms and disclosures and governs all offerings. Please consult with qualified professionals regarding your specific situation.
                  </div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== CTA SECTION ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-3xl p-8 lg:p-12 border border-white/10 relative overflow-hidden"
              >
                {/* Background accents */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/5 rounded-full blur-3xl" />
                
                <div className="relative z-10 text-center">
                  <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                    Have More Questions?
                  </h2>
                  <p className="text-white/50 max-w-xl mx-auto mb-8">
                    Schedule a consultation with our investor relations team to discuss your 
                    specific questions and learn how ASAP Capital can fit your investment goals.
                  </p>
                  
                  <div className="flex flex-wrap justify-center gap-4">
                    <Link to="/contact">
                      <button className="cta-contact px-6 py-3 h-12 rounded-full text-sm">
                        Schedule Consultation
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    </Link>
                    <Link to="/contact">
                      <button className="cta-contact px-6 py-3 h-12 rounded-full text-sm">
                        <Phone className="h-4 w-4" />
                        Contact Us
                      </button>
                    </Link>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default FAQPage;
